/**
 * Sticky bar while the draft has changes: how many, how many hours up and down,
 * and the two actions. While sending it shows the week being sent.
 */
import styled from 'styled-components'
import { fmtNum } from './imputacionLogic'

export interface SubmitProgress {
    chunk: number
    chunks: number
    savedHours: number
}

export interface SubmitBarProps {
    changes: number
    added: number
    removed: number
    submitting: boolean
    progress: SubmitProgress | null
    /** Status line instead of the progress ("Comprobando qué se guardó…"). */
    status?: string | null
    /** Why sending is not possible right now (invalid cells, read-only). */
    blockedReason?: string | null
    onDiscard: () => void
    onSubmit: () => void
}

const Bar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 20;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: 10px 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const Btn = styled.button<{ $primary?: boolean }>`
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 700;
  cursor: pointer;
  border: 1px solid white;
  background: ${({ $primary }) => ($primary ? 'white' : 'transparent')};
  color: ${({ $primary, theme }) => ($primary ? theme.colors.primary : 'white')};

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

const Blocked = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  opacity: 0.9;
`

export function SubmitBar({
    changes,
    added,
    removed,
    submitting,
    progress,
    status,
    blockedReason,
    onDiscard,
    onSubmit,
}: SubmitBarProps) {
    let text: string
    if (status) text = status
    else if (submitting && progress)
        text = `Imputando semana ${progress.chunk} de ${progress.chunks} · ${fmtNum(progress.savedHours)} h guardadas…`
    else text = `${changes} ${changes === 1 ? 'cambio' : 'cambios'} · +${fmtNum(added)} h / −${fmtNum(removed)} h`

    return (
        <Bar role="region" aria-label="Cambios sin enviar">
            <span aria-live="polite">{text}</span>
            <Actions>
                {blockedReason && !submitting && <Blocked>{blockedReason}</Blocked>}
                <Btn type="button" onClick={onDiscard} disabled={submitting}>
                    Descartar
                </Btn>
                <Btn type="button" $primary onClick={onSubmit} disabled={submitting || Boolean(blockedReason) || changes === 0}>
                    Imputar en Redmine
                </Btn>
            </Actions>
        </Bar>
    )
}
